/**
 * Certificat d'authenticité E031 — state machine + calls API.
 * Chargé sur la page /pages/certificat.
 *
 * Le composant lit `data-api-url` sur son container root pour connaître
 * l'URL de l'API (configurée via le settings de la section Shopify).
 */

(function () {
  'use strict';

  const STAGES = ['input', 'winner', 'form', 'done'];
  const LANG_STORAGE_KEY = 'lvc-cert-lang';

  const root = document.querySelector('[data-lvc-cert]');
  if (!root) return;

  const apiUrl = (root.getAttribute('data-api-url') || '').replace(/\/+$/, '');

  const el = (name) => root.querySelector(`[data-el="${name}"]`);
  const stages = {};
  STAGES.forEach((s) => {
    stages[s] = root.querySelector(`[data-stage="${s}"]`);
  });

  // ─── Bilingue FR/EN ───────────────────────────────────────────
  // Priorité : localStorage > <html lang="en"> > défaut FR.
  function detectInitialLang() {
    try {
      const stored = localStorage.getItem(LANG_STORAGE_KEY);
      if (stored === 'fr' || stored === 'en') return stored;
    } catch { /* ignore quota / privacy */ }
    const htmlLang = (document.documentElement.getAttribute('lang') || '').toLowerCase();
    if (htmlLang.startsWith('en')) return 'en';
    return 'fr';
  }

  const state = {
    stage: 'input',
    sessionToken: null,
    prizeId: null,
    isWinner: false,
    pair: null,
    lang: detectInitialLang(),
  };

  function applyLang(lang) {
    state.lang = lang;
    document.documentElement.classList.toggle('lang-en', lang === 'en');
    // Met à jour les boutons du switcher
    document
      .querySelectorAll('[data-el="lang-switcher"] .lang-switcher__btn')
      .forEach((btn) => {
        btn.classList.toggle(
          'lang-switcher__btn--active',
          btn.getAttribute('data-lang') === lang,
        );
      });
    // Met à jour le placeholder des <select> (option value="")
    document.querySelectorAll('select[data-placeholder-fr]').forEach((select) => {
      const first = select.querySelector('option[value=""]');
      if (!first) return;
      const attr = lang === 'en' ? 'data-placeholder-en' : 'data-placeholder-fr';
      const text = select.getAttribute(attr);
      if (text) first.textContent = text;
    });
    // Met à jour le bouton en cours de "Patientez…" si loading
    document.querySelectorAll('button[data-loading="1"]').forEach((btn) => {
      btn.textContent = lang === 'en' ? 'Please wait…' : 'Patientez…';
    });
    try {
      localStorage.setItem(LANG_STORAGE_KEY, lang);
    } catch { /* ignore */ }
  }

  // Init : applique la langue détectée au DOM avant tout affichage
  applyLang(state.lang);

  // Handler des boutons du switcher
  document
    .querySelectorAll('[data-el="lang-switcher"] .lang-switcher__btn')
    .forEach((btn) => {
      btn.addEventListener('click', () => {
        const target = btn.getAttribute('data-lang');
        if (target === 'fr' || target === 'en') applyLang(target);
      });
    });

  function setStage(next) {
    STAGES.forEach((s) => {
      if (stages[s]) stages[s].hidden = s !== next;
    });
    state.stage = next;
    hideError();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function showError(code) {
    const errEl = el('error');
    if (!errEl) return;
    errEl.textContent = translateError(code);
    errEl.hidden = false;
  }

  function hideError() {
    const errEl = el('error');
    if (errEl) errEl.hidden = true;
  }

  const ERRORS = {
    fr: {
      invalid_request: 'Le format saisi est invalide. Vérifiez le numéro de paire et le code.',
      invalid_credentials: 'Le code de vérification ne correspond pas à ce numéro de paire.',
      unknown_pair: "Ce numéro de paire n'existe pas dans notre registre.",
      already_activated: 'Cette paire a déjà été certifiée.',
      session_expired_or_invalid: 'Votre session a expiré. Veuillez recommencer depuis le début.',
      contact_required_for_winners: 'Merci de renseigner un email et un téléphone valides pour la remise de votre lot.',
      network_error: 'Impossible de contacter le service. Vérifiez votre connexion et réessayez.',
      fallback: 'Une erreur est survenue. Réessayez dans un instant.',
    },
    en: {
      invalid_request: 'The format entered is invalid. Please check the pair number and code.',
      invalid_credentials: 'The verification code does not match this pair number.',
      unknown_pair: 'This pair number does not exist in our records.',
      already_activated: 'This pair has already been certified.',
      session_expired_or_invalid: 'Your session has expired. Please start again from the beginning.',
      contact_required_for_winners: 'Please enter a valid email address and phone number for prize delivery.',
      network_error: 'Unable to reach the service. Please check your connection and try again.',
      fallback: 'An error occurred. Please try again in a moment.',
    },
  };

  function translateError(code) {
    const dict = ERRORS[state.lang] || ERRORS.fr;
    return dict[code] || dict.fallback;
  }

  function loadingLabel() {
    return state.lang === 'en' ? 'Please wait…' : 'Patientez…';
  }

  function setLoading(button, isLoading) {
    if (!button) return;
    button.disabled = isLoading;
    button.dataset.loading = isLoading ? '1' : '0';
    if (isLoading) {
      button.dataset.originalText = button.innerHTML;
      button.textContent = loadingLabel();
    } else if (button.dataset.originalText !== undefined) {
      button.innerHTML = button.dataset.originalText;
    }
  }

  async function apiCall(path, body) {
    try {
      const res = await fetch(`${apiUrl}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      let data = null;
      const contentType = res.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        data = await res.json();
      } else if (contentType.includes('application/pdf')) {
        return { ok: res.ok, blob: await res.blob() };
      }
      return { ok: res.ok, status: res.status, data };
    } catch {
      return { ok: false, status: 0, data: { error: 'network_error' } };
    }
  }

  // ─── Stage: input ─────────────────────────────────────────────
  const inputForm = el('input-form');
  if (inputForm) {
    inputForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const submitBtn = inputForm.querySelector('button[type="submit"]');
      hideError();

      const pair = el('input-pair').value.trim().padStart(4, '0');
      const code = el('input-code').value.trim().toUpperCase();

      setLoading(submitBtn, true);
      const res = await apiCall('/activate', { pair, code });
      setLoading(submitBtn, false);

      if (!res.ok) {
        showError(res.data?.error);
        return;
      }

      state.sessionToken = res.data.session_token;
      state.isWinner = res.data.is_winner;
      state.prizeId = res.data.prize_id || null;
      state.pair = pair;

      // Toggle des champs contact (email + phone) seulement pour les gagnants
      const contactFields = el('contact-fields');
      if (contactFields) contactFields.hidden = !state.isWinner;
      const emailInput = el('input-email');
      const phoneInput = el('input-phone');
      if (emailInput) emailInput.required = state.isWinner;
      if (phoneInput) phoneInput.required = state.isWinner;

      // Callout info sur le code de réclamation (gagnants uniquement)
      const winnerInfo = el('winner-info');
      if (winnerInfo) winnerInfo.hidden = !state.isWinner;

      if (state.isWinner) {
        showWinnerVariant(state.prizeId);
        showWinnerOverlay(); // stage courant = 'form' en interne, l'overlay est au-dessus
      } else {
        setStage('form');
      }
    });
  }

  // ─── Stage: winner ────────────────────────────────────────────
  function showWinnerVariant(prizeId) {
    root.querySelectorAll('[data-prize-id]').forEach((el) => {
      el.hidden = el.getAttribute('data-prize-id') !== prizeId;
    });
  }

  // ─── Overlay winner : plein écran, confettis, fondu auto 10s ──
  const WINNER_DURATION_MS = 20000;
  const CONFETTI_BURST_INTERVAL_MS = 900;
  const FADE_MS = 800;

  let confettiState = null;
  let winnerHideTimer = null;

  function showWinnerOverlay() {
    const overlay = el('winner-overlay');
    if (!overlay) return;

    // On prépare aussi le stage form en dessous : quand le fondu se termine,
    // le stage form doit être déjà en place. On le fait tout de suite.
    setStage('form');

    // Puis on ré-affiche l'overlay par-dessus
    overlay.hidden = false;
    overlay.classList.remove('winner-overlay--hiding');
    document.body.style.overflow = 'hidden';

    startConfetti();

    if (winnerHideTimer) clearTimeout(winnerHideTimer);
    winnerHideTimer = setTimeout(hideWinnerOverlay, WINNER_DURATION_MS);
  }

  function hideWinnerOverlay() {
    const overlay = el('winner-overlay');
    if (!overlay || overlay.hidden) return;
    if (winnerHideTimer) {
      clearTimeout(winnerHideTimer);
      winnerHideTimer = null;
    }
    overlay.classList.add('winner-overlay--hiding');
    setTimeout(() => {
      overlay.hidden = true;
      overlay.classList.remove('winner-overlay--hiding');
      document.body.style.overflow = '';
      stopConfetti();
      // Le stage form est déjà actif — on scroll doucement vers lui
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, FADE_MS);
  }

  // ─── Confetti : bursts répétés depuis le titre "Félicitations" ─
  function startConfetti() {
    stopConfetti();
    const canvas = el('confetti');
    const title = el('celebration-title');
    if (!canvas || !title) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      canvas.width = Math.floor(window.innerWidth * dpr);
      canvas.height = Math.floor(window.innerHeight * dpr);
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
    };
    resize();

    const colors = ['#c0392b', '#111', '#e2b83e', '#ffffff', '#2b5aa8', '#f39c12'];
    const gravity = 0.24 * dpr;
    const drag = 0.992;
    const wobble = 0.09;
    const particles = [];

    function originFromTitle() {
      const rect = title.getBoundingClientRect();
      return {
        x: (rect.left + rect.width / 2) * dpr,
        y: (rect.top + rect.height / 2) * dpr,
      };
    }

    function burst(count) {
      const { x: ox, y: oy } = originFromTitle();
      for (let i = 0; i < count; i++) {
        // angle réparti autour du cercle avec biais vers le haut
        const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.5;
        const speed = (5 + Math.random() * 8) * dpr;
        particles.push({
          x: ox,
          y: oy,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          w: (5 + Math.random() * 6) * dpr,
          h: (9 + Math.random() * 8) * dpr,
          color: colors[Math.floor(Math.random() * colors.length)],
          rot: Math.random() * Math.PI * 2,
          vr: (Math.random() - 0.5) * 0.35,
          life: 0,
          ttl: 2400 + Math.random() * 1000,
        });
      }
    }

    // Premier burst immédiat, puis intervalle
    burst(140);
    const burstTimer = setInterval(() => burst(80), CONFETTI_BURST_INTERVAL_MS);

    let running = true;
    let lastT = performance.now();

    function frame(now) {
      if (!running) return;
      const dt = Math.min(50, now - lastT);
      lastT = now;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.vx *= drag;
        p.vy = p.vy * drag + gravity;
        p.x += p.vx + Math.sin(now * 0.003 + p.rot) * wobble * dpr;
        p.y += p.vy;
        p.rot += p.vr;
        p.life += dt;

        if (p.life > p.ttl || p.y > canvas.height + 40) {
          particles.splice(i, 1);
          continue;
        }

        const fade = p.life > p.ttl - 400
          ? Math.max(0, (p.ttl - p.life) / 400)
          : 1;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.globalAlpha = fade;
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }

      requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);

    window.addEventListener('resize', resize, { passive: true });

    confettiState = {
      stop() {
        running = false;
        clearInterval(burstTimer);
        window.removeEventListener('resize', resize);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      },
    };
  }

  function stopConfetti() {
    if (confettiState) {
      confettiState.stop();
      confettiState = null;
    }
  }

  const winnerContinueBtn = el('winner-continue');
  if (winnerContinueBtn) {
    winnerContinueBtn.addEventListener('click', () => {
      hideWinnerOverlay();
    });
  }

  // ─── Stage: form ──────────────────────────────────────────────
  const genForm = el('generate-form');
  if (genForm) {
    genForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const submitBtn = genForm.querySelector('button[type="submit"]');
      hideError();

      const nom = el('input-nom').value.trim();
      const prenom = el('input-prenom').value.trim();
      const color = el('input-color').value;
      const size = el('input-size').value;
      const emailEl = el('input-email');
      const phoneEl = el('input-phone');
      const email = emailEl ? emailEl.value.trim() : '';
      const phone = phoneEl ? phoneEl.value.trim() : '';

      if (!nom || !prenom || !color || !size) {
        showError('invalid_request');
        return;
      }
      if (state.isWinner && (!email || !phone)) {
        showError('contact_required_for_winners');
        return;
      }

      setLoading(submitBtn, true);
      const res = await apiCall('/generate-cert', {
        session_token: state.sessionToken,
        nom,
        prenom,
        color,
        size,
        lang: state.lang,
        ...(email ? { email } : {}),
        ...(phone ? { phone } : {}),
      });
      setLoading(submitBtn, false);

      if (!res.ok) {
        showError(res.data?.error);
        return;
      }

      // Le résultat est un blob PDF — on force le téléchargement
      const blob = res.blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const filenamePrefix = state.lang === 'en' ? 'certificate' : 'certificat';
      a.download = `${filenamePrefix}-E031-${state.pair}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Petit délai pour éviter que la révocation casse le download
      setTimeout(() => URL.revokeObjectURL(url), 500);

      setStage('done');
    });
  }

  // ─── Stage: done — re-download button ─────────────────────────
  // Note : à ce stade la session est déjà consommée côté serveur,
  // impossible de re-télécharger sans passer par un nouveau flow.
  // Ce bouton reste indicatif.

  // Init
  setStage('input');
})();

/**
 * Peint le texte circulaire du hero en canvas 2D une seule fois, quand les
 * fonts sont prêtes. Résultat : rotation CSS pure sur un bitmap, aucune
 * re-rasterisation des lettres (contrairement à SVG textPath qui déforme).
 *
 * Attend un <canvas data-ring-canvas data-text="..." data-radius="0.78">.
 */
(function paintRingCanvases() {
  'use strict';

  const canvases = document.querySelectorAll('[data-ring-canvas]');
  if (!canvases.length) return;

  const paint = (canvas) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const text = canvas.dataset.text || '';
    const radiusRatio = parseFloat(canvas.dataset.radius || '0.78');
    const w = canvas.width;
    const h = canvas.height;
    const cx = w / 2;
    const cy = h / 2;
    const radius = (w / 2) * radiusRatio;

    // Taille de police proportionnelle à la largeur du canvas (échelle 2x pour crispness)
    const fontPx = Math.round(w * 0.052);

    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = '#c0392b';
    ctx.font = `900 ${fontPx}px "Work Sans", sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Répartit chaque lettre sur le cercle en tenant compte de sa largeur réelle
    const chars = Array.from(text);
    const widths = chars.map((c) => ctx.measureText(c).width);
    const totalArc = widths.reduce((s, w) => s + w, 0) + widths.length * 4;
    const scale = (2 * Math.PI * radius) / totalArc;

    let angle = -Math.PI / 2; // Démarre en haut
    for (let i = 0; i < chars.length; i++) {
      const cw = (widths[i] + 4) * scale;
      const charAngle = cw / radius;
      const midAngle = angle + charAngle / 2;

      ctx.save();
      ctx.translate(cx + radius * Math.cos(midAngle), cy + radius * Math.sin(midAngle));
      ctx.rotate(midAngle + Math.PI / 2);
      ctx.fillText(chars[i], 0, 0);
      ctx.restore();

      angle += charAngle;
    }
  };

  const paintAll = () => canvases.forEach(paint);

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(paintAll);
  } else {
    window.addEventListener('load', paintAll);
  }
})();
